<template>
  <div class="table-skeleton">
    <!-- Vista Desktop - Tabla -->
    <table class="skeleton-table hidden lg:table">
      <thead>
        <tr>
          <th v-for="column in columns" :key="column.key" :style="{ width: column.width }">
            <div class="skeleton-header" :style="{ width: column.headerWidth || '70%' }"></div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row" class="skeleton-row">
          <td v-for="column in columns" :key="column.key">
            <div v-if="column.type === 'text'" class="skeleton-cell-text"></div>
            <div v-else-if="column.type === 'toggle'" class="skeleton-cell-toggle"></div>
            <div v-else-if="column.type === 'date'" class="skeleton-cell-date">
              <div class="skeleton-icon"></div>
              <div class="skeleton-date-text"></div>
            </div>
            <div v-else-if="column.type === 'actions'" class="skeleton-cell-actions">
              <div class="skeleton-action-btn"></div>
              <div class="skeleton-action-btn"></div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Vista Mobile - Cards -->
    <div class="skeleton-cards lg:hidden mt-5 space-y-4 px-4 max-h-[600px] overflow-y-auto">
      <div v-for="row in rows" :key="row" class="skeleton-card">
        <!-- Header de la card -->
        <div class="skeleton-card-header">
          <div class="skeleton-card-header-content">
            <div class="skeleton-card-title"></div>
            <div class="skeleton-card-date-small"></div>
          </div>
          <div class="skeleton-card-toggle"></div>
        </div>

        <!-- Contenido de la card -->
        <div class="skeleton-card-body">
          <!-- Campo principal -->
          <div class="skeleton-card-field">
            <div class="skeleton-field-label"></div>
            <div class="skeleton-field-value"></div>
          </div>

          <!-- Campos en grid (si hay más columnas) -->
          <div v-if="columns.length > 3" class="skeleton-card-grid">
            <div class="skeleton-card-field">
              <div class="skeleton-field-label"></div>
              <div class="skeleton-field-value-small"></div>
            </div>
            <div class="skeleton-card-field">
              <div class="skeleton-field-label"></div>
              <div class="skeleton-field-value-small"></div>
            </div>
          </div>

          <!-- Estado badge -->
          <div class="skeleton-card-status">
            <div class="skeleton-status-label"></div>
            <div class="skeleton-status-badge"></div>
          </div>

          <!-- Botones de acción -->
          <div class="skeleton-card-actions">
            <div class="skeleton-action-button"></div>
            <div class="skeleton-action-button"></div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Paginación skeleton -->
    <div v-if="showPagination" class="skeleton-pagination hidden lg:flex">
      <div class="skeleton-pagination-info"></div>
      <div class="skeleton-pagination-buttons">
        <div class="skeleton-page-btn"></div>
        <div class="skeleton-page-btn active"></div>
        <div class="skeleton-page-btn"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableSkeleton',
  props: {
    rows: {
      type: Number,
      default: 6
    },
    columns: {
      type: Array,
      default: () => [
        { key: 'marca', width: '12%', type: 'text', headerWidth: '40%' },
        { key: 'descripcion', width: '22%', type: 'text', headerWidth: '60%' },
        { key: 'visibilidad', width: '15%', type: 'toggle', headerWidth: '70%' },
        { key: 'fecha', width: '28%', type: 'date', headerWidth: '65%' },
        { key: 'acciones', width: '13%', type: 'actions', headerWidth: '50%' }
      ]
    },
    showPagination: {
      type: Boolean,
      default: true
    }
  }
}
</script>

<style scoped>
/* ========== CONTENEDOR PRINCIPAL ========== */
.table-skeleton {
  width: 100%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

@media (max-width: 1023px) {
  .table-skeleton {
    background: transparent;
    box-shadow: none;
  }
}

/* ========== VISTA DESKTOP - TABLA ========== */
.skeleton-table {
  width: 100%;
  border-collapse: collapse;
}

.skeleton-table thead {
  background: linear-gradient(to right, rgb(248, 250, 252), rgb(241, 245, 249));
  border-bottom: 1px solid #e8eaed;
}

.skeleton-table th {
  padding: 16px 24px;
  text-align: left;
  font-weight: 600;
}

.skeleton-header {
  height: 14px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

.skeleton-row {
  border-bottom: 1px solid #f0f2f5;
  transition: background-color 0.2s;
}

.skeleton-row:hover {
  background-color: #fafbfc;
}

.skeleton-row:last-child {
  border-bottom: none;
}

.skeleton-row td {
  padding: 20px 24px;
  vertical-align: middle;
}

/* Skeleton para texto */
.skeleton-cell-text {
  height: 16px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
  width: 75%;
}

/* Skeleton para toggle/switch */
.skeleton-cell-toggle {
  width: 48px;
  height: 26px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 13px;
}

/* Skeleton para fecha con icono */
.skeleton-cell-date {
  display: flex;
  align-items: center;
  gap: 10px;
}

.skeleton-icon {
  width: 18px;
  height: 18px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 4px;
  flex-shrink: 0;
}

.skeleton-date-text {
  height: 16px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
  width: 180px;
}

/* Skeleton para botones de acción */
.skeleton-cell-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.skeleton-action-btn {
  width: 36px;
  height: 36px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

/* ========== VISTA MOBILE - CARDS ========== */
.skeleton-cards {
  display: flex;
  flex-direction: column;
}

.skeleton-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.skeleton-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
}

/* Header de la card */
.skeleton-card-header {
  background: linear-gradient(to right, rgb(248, 250, 252), rgb(241, 245, 249));
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.skeleton-card-header-content {
  flex: 1;
  min-width: 0;
}

.skeleton-card-title {
  height: 18px;
  width: 65%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
  margin-bottom: 8px;
}

.skeleton-card-date-small {
  height: 12px;
  width: 40%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-card-toggle {
  width: 44px;
  height: 24px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 12px;
  flex-shrink: 0;
}

/* Body de la card */
.skeleton-card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-card-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-field-label {
  height: 10px;
  width: 30%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-field-value {
  height: 14px;
  width: 80%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

.skeleton-field-value-small {
  height: 14px;
  width: 60%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

/* Grid para campos en dos columnas */
.skeleton-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* Estado badge */
.skeleton-card-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.skeleton-status-label {
  height: 10px;
  width: 25%;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-status-badge {
  height: 24px;
  width: 70px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 12px;
}

/* Botones de acción mobile */
.skeleton-card-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

.skeleton-action-button {
  flex: 1;
  height: 42px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 8px;
}

/* ========== PAGINACIÓN ========== */
.skeleton-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #f0f2f5;
  background: #fafbfc;
}

.skeleton-pagination-info {
  width: 80px;
  height: 14px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

.skeleton-pagination-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.skeleton-page-btn {
  width: 36px;
  height: 36px;
  background: linear-gradient(90deg, #e8eaed 25%, #f5f6f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 6px;
}

.skeleton-page-btn.active {
  background: linear-gradient(90deg, #1e293b 25%, #334155 50%, #1e293b 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
}

/* ========== ANIMACIÓN SHIMMER ========== */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* ========== DELAYS ESCALONADOS ========== */
/* Desktop - Tabla */
.skeleton-row:nth-child(1) .skeleton-cell-text,
.skeleton-row:nth-child(1) .skeleton-cell-toggle,
.skeleton-row:nth-child(1) .skeleton-date-text,
.skeleton-row:nth-child(1) .skeleton-action-btn {
  animation-delay: 0s;
}

.skeleton-row:nth-child(2) .skeleton-cell-text,
.skeleton-row:nth-child(2) .skeleton-cell-toggle,
.skeleton-row:nth-child(2) .skeleton-date-text,
.skeleton-row:nth-child(2) .skeleton-action-btn {
  animation-delay: 0.1s;
}

.skeleton-row:nth-child(3) .skeleton-cell-text,
.skeleton-row:nth-child(3) .skeleton-cell-toggle,
.skeleton-row:nth-child(3) .skeleton-date-text,
.skeleton-row:nth-child(3) .skeleton-action-btn {
  animation-delay: 0.2s;
}

.skeleton-row:nth-child(4) .skeleton-cell-text,
.skeleton-row:nth-child(4) .skeleton-cell-toggle,
.skeleton-row:nth-child(4) .skeleton-date-text,
.skeleton-row:nth-child(4) .skeleton-action-btn {
  animation-delay: 0.3s;
}

.skeleton-row:nth-child(5) .skeleton-cell-text,
.skeleton-row:nth-child(5) .skeleton-cell-toggle,
.skeleton-row:nth-child(5) .skeleton-date-text,
.skeleton-row:nth-child(5) .skeleton-action-btn {
  animation-delay: 0.4s;
}

.skeleton-row:nth-child(6) .skeleton-cell-text,
.skeleton-row:nth-child(6) .skeleton-cell-toggle,
.skeleton-row:nth-child(6) .skeleton-date-text,
.skeleton-row:nth-child(6) .skeleton-action-btn {
  animation-delay: 0.5s;
}

/* Mobile - Cards */
.skeleton-card:nth-child(1) * {
  animation-delay: 0s;
}

.skeleton-card:nth-child(2) * {
  animation-delay: 0.1s;
}

.skeleton-card:nth-child(3) * {
  animation-delay: 0.2s;
}

.skeleton-card:nth-child(4) * {
  animation-delay: 0.3s;
}

.skeleton-card:nth-child(5) * {
  animation-delay: 0.4s;
}

.skeleton-card:nth-child(6) * {
  animation-delay: 0.5s;
}

/* Utility classes */
.space-y-4 > * + * {
  margin-top: 1rem;
}

@media (min-width: 1024px) {
  .lg\:hidden {
    display: none;
  }
  
  .lg\:table {
    display: table;
  }
  
  .lg\:flex {
    display: flex;
  }
}

@media (max-width: 1023px) {
  .hidden {
    display: none;
  }
}
</style>